import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Search, Plus, Phone, Mail, MapPin, FolderKanban, Pencil, Trash2, Users, Tag, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = { nazwa: '', email: '', telefon: '', adres: '', notatki: '', tags: [] };

const TAG_COLORS = {
  'VIP': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Deweloper': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Indywidualny': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Reklamacja': 'bg-red-500/20 text-red-400 border-red-500/30',
};

export const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [newTag, setNewTag] = useState('');

  const isAdmin = user?.role === 'admin';

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const [clientsRes, tagsRes] = await Promise.all([
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/client-tags`)
      ]);
      setClients(clientsRes.data);
      setAvailableTags(tagsRes.data);
    } catch {
      toast.error('Błąd pobierania klientów');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    if (!search && !tagFilter) { setFiltered(clients); return; }
    let result = clients;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.nazwa?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.telefon?.toLowerCase().includes(q) ||
        c.adres?.toLowerCase().includes(q)
      );
    }
    if (tagFilter) {
      result = result.filter(c => (c.tags || []).includes(tagFilter));
    }
    setFiltered(result);
  }, [search, tagFilter, clients]);

  const openNew = () => { setEditingClient(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditingClient(c);
    setForm({ nazwa: c.nazwa, email: c.email || '', telefon: c.telefon || '', adres: c.adres || '', notatki: c.notatki || '', tags: c.tags || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nazwa.trim()) { toast.error('Nazwa klienta jest wymagana'); return; }
    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/clients/${editingClient.id}`, form);
        toast.success('Klient zaktualizowany');
      } else {
        await axios.post(`${API_URL}/clients`, form);
        toast.success('Klient dodany');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego klienta? Projekty zostaną odpięte.')) return;
    try {
      await axios.delete(`${API_URL}/clients/${id}`);
      toast.success('Klient usunięty');
      fetchClients();
    } catch {
      toast.error('Błąd usuwania klienta');
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Wyodrębnić klientów z istniejących projektów? Nowi klienci zostaną utworzeni automatycznie.')) return;
    try {
      const res = await axios.post(`${API_URL}/clients/migrate`);
      toast.success(res.data.message);
      fetchClients();
    } catch {
      toast.error('Błąd migracji');
    }
  };

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  const addCustomTag = async () => {
    const t = newTag.trim();
    if (!t) return;
    if (!availableTags.includes(t)) {
      try { await axios.post(`${API_URL}/client-tags`, { name: t }); } catch {}
      setAvailableTags(prev => [...prev, t]);
    }
    if (!form.tags.includes(t)) setForm(prev => ({ ...prev, tags: [...prev.tags, t] }));
    setNewTag('');
  };

  const getTagColor = (tag) => TAG_COLORS[tag] || 'bg-slate-500/20 text-[var(--crm-text-secondary)] border-slate-500/30';

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight" data-testid="clients-title">Klienci</h1>
            <p className="text-[var(--crm-text-secondary)] mt-1">Zarządzaj bazą klientów ({clients.length})</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button onClick={handleMigrate} data-testid="migrate-clients-btn"
                title="Automatycznie tworzy klientów z istniejących projektów"
                className="bg-amber-600 hover:bg-amber-700 text-white">
                Migruj z projektów
              </Button>
            )}
            <Button onClick={openNew} data-testid="add-client-btn"
              className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white">
              <Plus className="w-4 h-4 mr-2" />Nowy Klient
            </Button>
          </div>
        </div>

        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 mb-6">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)]" />
              <Input
                placeholder="Szukaj: nazwa, email, telefon, adres..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="search-clients-input"
                className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
              />
            </div>
            {availableTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-[var(--crm-text-muted)] shrink-0" />
                {availableTags.map(t => (
                  <button key={t} onClick={() => setTagFilter(tagFilter === t ? '' : t)}
                    data-testid={`tag-filter-${t}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      tagFilter === t ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : getTagColor(t)
                    }`}>
                    {t}
                  </button>
                ))}
                {tagFilter && (
                  <button onClick={() => setTagFilter('')} className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>

        {loading ? (
          <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie klientów...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-12 text-center">
            <Users className="w-12 h-12 text-[var(--crm-text-muted)] mx-auto mb-3" />
            <p className="text-[var(--crm-text-secondary)]">{search ? 'Brak wyników wyszukiwania' : 'Brak klientów. Dodaj pierwszego klienta lub uruchom migrację.'}</p>
          </Card>
        ) : (
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium">Nazwa</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium hidden sm:table-cell">Tagi</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium hidden md:table-cell">Telefon</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium hidden lg:table-cell">Email</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium text-center">Projektów</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium text-right">Wartość</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-medium text-right w-20">Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(c => (
                    <TableRow key={c.id} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)] cursor-pointer"
                      data-testid={`client-row-${c.id}`}
                      onClick={() => navigate(`/klienci/${c.id}`)}>
                      <TableCell className="font-medium text-[var(--crm-text-primary)]">{c.nazwa}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).map(t => (
                            <span key={t} className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${getTagColor(t)}`}>{t}</span>
                          ))}
                          {(c.projects_count || 0) > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/30 inline-flex items-center gap-0.5">
                              <RefreshCw className="w-2.5 h-2.5" />Powracający
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[var(--crm-text-secondary)] hidden md:table-cell">
                        {c.telefon ? <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[var(--crm-text-muted)]" />{c.telefon}</span> : '—'}
                      </TableCell>
                      <TableCell className="text-[var(--crm-text-secondary)] hidden lg:table-cell">
                        {c.email ? <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[var(--crm-text-muted)]" />{c.email}</span> : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 text-blue-400">
                          <FolderKanban className="w-3.5 h-3.5" />{c.projects_count || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-[var(--crm-text-secondary)] font-mono text-sm">
                        {(c.total_offer || 0).toLocaleString('pl-PL', { minimumFractionDigits: 0 })} zł
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}
                            className="h-8 w-8 p-0 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid={`delete-client-${c.id}`}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-[var(--crm-text-primary)] max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edytuj klienta' : 'Nowy klient'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Nazwa *</Label>
              <Input value={form.nazwa} onChange={(e) => setForm({...form, nazwa: e.target.value})}
                data-testid="client-form-nazwa" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Telefon</Label>
                <Input value={form.telefon} onChange={(e) => setForm({...form, telefon: e.target.value})}
                  data-testid="client-form-telefon" placeholder="+48 123 456 789"
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  data-testid="client-form-email" placeholder="klient@firma.pl"
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Adres</Label>
              <Input value={form.adres} onChange={(e) => setForm({...form, adres: e.target.value})}
                data-testid="client-form-adres" placeholder="ul. Przykładowa 1, Warszawa"
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Notatki</Label>
              <Textarea value={form.notatki} onChange={(e) => setForm({...form, notatki: e.target.value})}
                data-testid="client-form-notatki" rows={3} placeholder="Dodatkowe informacje o kliencie..."
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Tagi</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {availableTags.map(t => (
                  <button key={t} type="button" onClick={() => toggleTag(t)}
                    data-testid={`tag-toggle-${t}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      form.tags.includes(t)
                        ? 'bg-[#FF6B35] text-white border-[#FF6B35] ring-1 ring-[#FF6B35]/50'
                        : getTagColor(t) + ' opacity-60 hover:opacity-100'
                    }`}>
                    {form.tags.includes(t) && '✓ '}{t}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                  data-testid="client-form-new-tag"
                  placeholder="Nowy tag..."
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-sm h-8" />
                <Button type="button" onClick={addCustomTag} size="sm"
                  data-testid="client-form-add-tag-btn"
                  className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-btn-secondary-hover)] text-[var(--crm-text-primary)] h-8 px-3 shrink-0">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]">Anuluj</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="client-form-save"
              className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white">
              {saving ? 'Zapisywanie...' : editingClient ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
